/**
 * Set your password -- the page an invite or password-reset email lands on.
 *
 * R-B: this file used to be a self-registration form that faked success (a 1-second
 * timeout, then /home) and even offered the Admin role. Sempirical is invite-only and
 * there is no self-registration, so /register now redirects home (App.jsx), and this
 * page does the one real thing an invited user needs: set their own password.
 *
 * Flow: an admin invites the user (Security page, or the Supabase dashboard) -> Supabase
 * emails a link -> the link opens this page -> POST /api/auth/password with a short-lived
 * session token -> the user signs in normally (admins then enroll their authenticator).
 *
 * TWO LINK SHAPES, both handled:
 *   ?token_hash=...&type=invite|recovery|magiclink|email   (current email templates)
 *       Opening the page consumes NOTHING. Corporate mail scanners fetch every link
 *       seconds after delivery; when the link went straight to Supabase's GET /verify,
 *       that fetch used up the one-time token and the person's own click failed
 *       ("Email link is invalid or has expired", 2026-10-05). So the token is redeemed
 *       only when the person clicks the button: POST /api/auth/verify-link -> session.
 *   #access_token=...&type=invite|recovery                 (links already sent)
 *       Supabase already verified the link and put a session in the fragment.
 * The token is read once, removed from the address bar, and never stored.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE } from '../../config'
import './Register.css'
import '../SignIn/SignIn.css'

const MIN_LENGTH = 12

function readLink() {
  const fragment = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
  const query = new URLSearchParams(window.location.search || '')
  const accessToken = fragment.get('access_token')
  return {
    accessToken,
    tokenHash: accessToken ? null : query.get('token_hash'),
    type: fragment.get('type') || query.get('type'),
    error: fragment.get('error_description') || fragment.get('error'),
  }
}

// What the confirm step says before the person's click redeems the link.
const CONFIRM = {
  invite: { title: "You're invited to Sempirical", body: 'Accept the invitation, then choose your password.', button: 'Accept invite and set password' },
  recovery: { title: 'Reset your password', body: 'Continue to choose a new password for your Sempirical account.', button: 'Continue' },
  magiclink: { title: 'Continue to Sempirical', body: 'Continue to confirm this link, then choose your password.', button: 'Continue' },
}
CONFIRM.email = CONFIRM.magiclink

export default function Register() {
  const [link, setLink] = useState({ accessToken: null, tokenHash: null, type: null, error: null })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // Page load: READ the link only. A fragment session is removed from the address bar;
  // a ?token_hash link stays put (a refresh still works) until the person's click uses it.
  useEffect(() => {
    const parsed = readLink()
    setLink(parsed)
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [])

  // The person's click -- the ONLY place the one-time link is redeemed.
  async function acceptLink() {
    setError('')
    setVerifying(true)
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token_hash: link.tokenHash, type: link.type }),
      })
      let data = {}
      try { data = await res.json() } catch { /* not JSON */ }
      if (res.ok && data.token) {
        window.history.replaceState(null, '', window.location.pathname)
        setLink({ accessToken: data.token, tokenHash: null, type: link.type, error: null })
        return
      }
      if (res.status === 429) setError('Too many attempts. Wait a few minutes and try again.')
      else if (res.status === 503) setError('Sign-in service unavailable. Try again shortly.')
      else setError(data.detail || 'This link could not be used. Ask your admin for a new one.')
    } catch {
      setError('Connection error. Try again.')
    } finally {
      setVerifying(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (password.length < MIN_LENGTH) { setError(`Use at least ${MIN_LENGTH} characters.`); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/api/auth/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${link.accessToken}` },
        body: JSON.stringify({ password }),
      })
      if (res.ok) { setDone(true); return }
      let detail = ''
      try { detail = (await res.json()).detail || '' } catch { /* not JSON */ }
      if (res.status === 401) setError('This link has expired or was already used. Ask your admin for a new one.')
      else if (detail === 'mfa_required') setError('This account already has an authenticator. Sign in with it first, or ask an admin to reset it.')
      else setError(detail || 'Your password was not accepted.')
    } catch {
      setError('Connection error. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const heading = link.type === 'recovery' ? 'Reset your password' : 'Set your password'

  return (
    <div className="reg-page si-page">
      <div className="reg-card si-card">
        <div className="reg-logo">
          <img src="/sempirical-lockup.svg" alt="Sempirical" className="reg-wordmark" />
        </div>

        {done ? (
          <>
            <h1 className="reg-title">Password set</h1>
            <p className="reg-subtitle">Sign in with your email and your new password.</p>
            <Link to="/login" className="reg-submit" style={{ textAlign: 'center', textDecoration: 'none' }}>Go to sign in</Link>
          </>
        ) : !link.accessToken && link.tokenHash ? (
          <>
            <h1 className="reg-title">{(CONFIRM[link.type] || CONFIRM.invite).title}</h1>
            <p className="reg-subtitle">{(CONFIRM[link.type] || CONFIRM.invite).body}</p>
            {error && <div className="reg-error">{error}</div>}
            <button type="button" className="reg-submit" onClick={acceptLink} disabled={verifying}>
              {verifying ? 'Checking link…' : (CONFIRM[link.type] || CONFIRM.invite).button}
            </button>
          </>
        ) : !link.accessToken ? (
          <>
            <h1 className="reg-title">Invitation required</h1>
            <p className="reg-subtitle">
              {link.error
                ? `This link could not be used: ${link.error}. Ask your admin for a new invite or reset link.`
                : 'Sempirical is invite-only. Open this page from the invite or password-reset email your admin sent you.'}
            </p>
          </>
        ) : (
          <>
            <h1 className="reg-title">{heading}</h1>
            <p className="reg-subtitle">Choose a password of at least {MIN_LENGTH} characters.</p>
            {error && <div className="reg-error">{error}</div>}
            <form onSubmit={handleSubmit} className="reg-form">
              <div className="reg-field">
                <label className="reg-label">New password</label>
                <input className="reg-input" type="password" autoComplete="new-password"
                  value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <div className="reg-field">
                <label className="reg-label">Confirm password</label>
                <input className="reg-input" type="password" autoComplete="new-password"
                  value={confirm} onChange={e => setConfirm(e.target.value)} />
              </div>
              <button type="submit" className="reg-submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Set password'}
              </button>
            </form>
          </>
        )}

        <div className="reg-footer">
          Already set up? <Link to="/login" className="reg-link">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
